import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AsyncSearchBarV2, InteractiveContentCard } from '@amzn/seller-university-ui-components';
import { useLazyAutocompleteV2Query } from 'src/redux/slices/api/su';
import { useClientId, useSelLocale } from 'src/contexts/ClientContext';
import useMetrics from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';
import { ROUTES } from 'src/constants/route';
import { API_SEARCH_PARAMS, AUTOCOMPLETE_V2_PARAM } from 'src/constants/api';
import { Suggestion, SearchItem, ContentType } from 'src/types/api/autocomplete';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/utils/translation';
import { BUNDLE_NAME, NAVIGATION_STRING_ID, SEARCH_STRING_ID } from 'src/constants/localization';
import { getAppRoute } from 'src/utils/routing';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';

export interface AutoCompleteSearchWrapperV2Props {
  placeholder?: string;
  maxSuggestions?: number;
  maxModules?: number;
  katAriaLabel?: string;
  disabled?: boolean;
  throttleMs?: number;
}

// Union type for combined search results
type AutocompleteV2Result = Suggestion | SearchItem;

const isSearchItem = (result: AutocompleteV2Result): result is SearchItem => {
  return 'resourceId' in result && 'resourceType' in result;
};

/**
 * AutoCompleteSearchWrapperV2 Component
 *
 * A wrapper component that integrates AsyncSearchBarV2 with AutoCompleteV2 API functionality.
 * This component supports both text suggestions and module results in a single search experience.
 *
 */
const AutoCompleteSearchWrapperV2 = React.forwardRef<{ triggerSearch:() => void }, AutoCompleteSearchWrapperV2Props>(({
  placeholder,
  maxSuggestions = AUTOCOMPLETE_V2_PARAM.MAX_SUGGESTIONS,
  maxModules = AUTOCOMPLETE_V2_PARAM.MAX_MODULES,
  katAriaLabel = AUTOCOMPLETE_V2_PARAM.ARIA_LABEL,
  disabled = AUTOCOMPLETE_V2_PARAM.DISABLED,
  throttleMs = AUTOCOMPLETE_V2_PARAM.THROTTLE_MS,
}, ref) => {
  const navigate = useNavigate();
  const clientId = useClientId();
  const selLocale = useSelLocale();
  const { clickMetric } = useMetrics();
  const [navigationBundle] = useBundle(BUNDLE_NAME.NAVIGATION);
  const [searchBundle] = useBundle(BUNDLE_NAME.SEARCH);

  const searchPlaceholder = placeholder || t(navigationBundle, NAVIGATION_STRING_ID.SEARCH_PLACEHOLDER);
  const failureMessage = t(searchBundle, SEARCH_STRING_ID.AUTOCOMPLETE_FAILURE_MESSAGE);
  const loadingMessage = t(searchBundle, SEARCH_STRING_ID.AUTOCOMPLETE_LOADING_MESSAGE);
  const [currentSearchValue, setCurrentSearchValue] = React.useState('');
  const [resetKey, setResetKey] = React.useState(0);

  const [triggerAutocompleteV2] = useLazyAutocompleteV2Query();

  // Handle async search input changes
  const handleInput = useCallback(
    async (inputSearchTerm: string): Promise<AutocompleteV2Result[]> => {
      const trimmedInput = inputSearchTerm.trim();

      // Only make API calls for non-empty, non-whitespace entries with at least 2 characters
      if (!trimmedInput || trimmedInput.length < 2) {
        return [];
      }

      try {
        const result = await triggerAutocompleteV2({
          query: trimmedInput,
          maxSuggestions,
          maxModules,
          clientId,
          selLocale,
        }).unwrap();

        const validModules = (result?.modules || [])
          .filter((module) => module.resourceType
            && Object.values(ContentType).includes(module.resourceType as ContentType));

        // Combine suggestions and valid modules into a single array
        const combinedResults: AutocompleteV2Result[] = [
          ...(result?.suggestions || []),
          ...validModules,
        ];

        return combinedResults;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Autocomplete V2 API request failed';
        // Log as warn since API errors are already handled with proper severity in baseQuery.ts
        ClientLogger.warn(ClientLog.PayloadProcessingError, {
          error: errorMessage,
        });
        return [];
      }
    },
    [triggerAutocompleteV2, maxSuggestions, maxModules, clientId],
  );

  // Handle search submission
  const handleSubmit = useCallback(
    (searchQuery: string) => {
      setCurrentSearchValue(searchQuery);

      const trimmed = searchQuery.trim();
      if (trimmed) {
        clickMetric({ feature: FeatureName.NavSearchSubmit, metadata: { searchQueryLength: trimmed.length } });
        navigate(getAppRoute(`${ROUTES.NEW_LEARN.SEARCH}?${API_SEARCH_PARAMS.QUERY}=`
            + `${encodeURIComponent(trimmed)}`));
      }
    },
    [navigate, clickMetric],
  );

  // Handle result selection
  const handleSelect = useCallback(
    (result: AutocompleteV2Result) => {
      if (isSearchItem(result)) {
        if (result.resourceType === ContentType.MODULE) {
          navigate(getAppRoute(`${ROUTES.LEARN.LEARNING_MODULE}/${result.resourceId}`));
        }
        // Future: Handle other resource types
        return;
      }
      handleSubmit(result.query);
    },
    [navigate, handleSubmit],
  );

  // Render individual search results
  const renderResult = useCallback(
    (result: AutocompleteV2Result, index: number): JSX.Element | null => {
      if (isSearchItem(result)) {
        if (result.resourceType === ContentType.MODULE) {
          return (
            <InteractiveContentCard
              key={`module-${result.resourceId}`}
              imageSrc={result.thumbnailLink || ''}
              title={result.title}
              previewDescription={result.description || ''}
              metadata={[]}
              layout="responsive"
              onClick={() => handleSelect(result)}
            />
          );
        }
        // Future: Handle other resource types (PATHWAY, TOPIC)
        return null;
      }
      return (
        <div
          key={`suggestion-${index}`}
          className="w-full px-su-space-09 py-su-space-04"
        >
          {result.query}
        </div>
      );
    },
    [handleSelect],
  );

  // Generate unique keys for results
  const getResultKey = useCallback(
    (result: AutocompleteV2Result, index: number): string => {
      if (isSearchItem(result)) {
        return `module-${result.resourceId}`;
      }
      return `suggestion-${index}-${result.query}`;
    },
    [],
  );

  React.useImperativeHandle(ref, () => ({
    triggerSearch: () => {
      setResetKey((prev) => prev + 1);
      handleSubmit(currentSearchValue);
    },
  }), [handleSubmit, currentSearchValue]);

  const handleChange = (value: string) => {
    setCurrentSearchValue(value);
  };

  return (
    <AsyncSearchBarV2<AutocompleteV2Result>
      key={resetKey}
      placeholder={searchPlaceholder}
      value={currentSearchValue}
      onInputChange={handleInput}
      onChange={handleChange}
      onSelect={handleSelect}
      onSubmit={handleSubmit}
      renderResult={renderResult}
      getResultKey={getResultKey}
      failureMessage={failureMessage}
      loadingMessage={loadingMessage}
      throttleMs={throttleMs}
      kat-aria-label={katAriaLabel}
      disabled={disabled}
    />
  );
});

AutoCompleteSearchWrapperV2.displayName = 'AutoCompleteSearchWrapperV2';

export default AutoCompleteSearchWrapperV2;
